/**
 * Triage Status Badge Component
 * 
 * Displays the triage status of an item with appropriate styling.
 * Shows whether an item has been triaged and, if so, the outcome.
 */

import { CheckCircle, Clock, XCircle, HelpCircle, ArrowRightCircle, AlertTriangle } from 'lucide-react';
import { useTriageStore } from '../../stores/prioritization/triageStore';
import type { TriageOutcome } from '../../types/prioritization/triage';

export interface TriageStatusBadgeProps {
  /** The item ID to check triage status for */
  itemId: string;
  /** Whether to show the full outcome label or just the status */
  showOutcome?: boolean;
  /** When true, show "Direct" instead of "Not Triaged" when there is no triage assessment */
  isDirect?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional class names */
  className?: string;
}

const OUTCOME_CONFIG: Record<TriageOutcome, { 
  label: string; 
  icon: typeof CheckCircle; 
  colors: string;
}> = {
  pursue: {
    label: 'Pursue',
    icon: CheckCircle,
    colors: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
  },
  defer: {
    label: 'Deferred',
    icon: Clock,
    colors: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]',
  },
  reject: {
    label: 'Rejected',
    icon: XCircle,
    colors: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
  },
  needs_clarification: {
    label: 'Needs Info',
    icon: HelpCircle,
    colors: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  },
  redirect: {
    label: 'Redirected',
    icon: ArrowRightCircle,
    colors: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  },
};

export function TriageStatusBadge({ 
  itemId, 
  showOutcome = false, 
  isDirect = false,
  size = 'sm',
  className = '',
}: TriageStatusBadgeProps) {
  const assessment = useTriageStore(state => state.getTriageAssessment(itemId));
  
  const sizeClasses = size === 'sm' 
    ? 'px-2 py-0.5 text-xs gap-1' 
    : 'px-2.5 py-1 text-sm gap-1.5';
  
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  
  // Not triaged — show "Direct" for directly-submitted items, otherwise "Not Triaged"
  if (!assessment) {
    if (isDirect) {
      return (
        <span 
          className={`inline-flex items-center ${sizeClasses} rounded-full font-medium border bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] ${className}`}
          title="Submitted directly (bypasses triage)"
        >
          <span>Direct</span>
        </span>
      );
    }
    return (
      <span 
        className={`inline-flex items-center ${sizeClasses} rounded-full font-medium border bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] ${className}`}
        title="This item has not been triaged yet"
      >
        <AlertTriangle className={iconSize} />
        <span>Not Triaged</span>
      </span>
    );
  }
  
  // Triaged - show outcome if requested
  const config = OUTCOME_CONFIG[assessment.outcome];
  const Icon = config.icon;
  
  if (showOutcome) {
    return (
      <span 
        className={`inline-flex items-center ${sizeClasses} rounded-full font-medium border ${config.colors} ${className}`}
        title={`Triaged: ${config.label}`}
      >
        <Icon className={iconSize} />
        <span>{config.label}</span>
      </span>
    );
  }
  
  // Simple "Triaged" badge
  return (
    <span 
      className={`inline-flex items-center ${sizeClasses} rounded-full font-medium border bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)] ${className}`}
      title={`Triaged: ${config.label}`}
    >
      <CheckCircle className={iconSize} />
      <span>Triaged</span>
    </span>
  );
}

export default TriageStatusBadge;
